import * as React from 'react';
import { Route, BrowserRouter as Router, NavLink, useLocation, useNavigate, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../shared/internationalization/I18nSetting';
import BtsPage from './BtsPage';
import { Provider } from 'mobx-react';
import PolaPolarStudioHomePage from './PolaPolarStudioHomePage';
import CategoryPage from './CategoryPage';
import LicensePage from './LicensePage';
import ModePage from './ModePage';
import StudioUserStore from '../stores/StudioUserStore';
import CategoryPackagePage from './CategoryPackagePage';
import { inject, observer } from "mobx-react";

import PoluvLogo from "../../assets/img/PoluvFrame.png";
import { Button, Form, Navbar, Nav } from 'react-bootstrap';
import { Clock, JournalBookmark, PersonCircle } from 'react-bootstrap-icons';
import BlockKitStore from '../stores/BlockKitStore';
import PublishedCategoryPage from './PublishedCategoryPage';
import PublishedBlockPage from './PublishedBlockPage';
import LayoutPage from './LayoutPage';
import TemplatePage from './TemplatePage';
import SalesStatisticsPage from './SalesStatisticsPage';
import SalesDetailPage from './SalesDetailPage';
import AccountPermissionPage from './AccountPermissionPage';
import NavDropdown from '../components/NavDropdown/NavDropdown';
import { NativeLanguageNameMap } from '../constants/LanguageCode';
import BlockPage from './BlockPage';

export interface BtsIndexProps {
  studioUserStore?: StudioUserStore;
  blockKitStore?: BlockKitStore;
}

const getInitialLanguage = (): string => {
  const browserLang = navigator.language;
  const supported = NativeLanguageNameMap.map(l => l.code);
  
  if ((supported as string[]).includes(browserLang)) return browserLang;
  return "ko-KR";
};

const BtsIndex: React.FC<BtsIndexProps> = inject("studioUserStore", "blockKitStore")(observer(({studioUserStore, blockKitStore }) => {
  const { t } = useTranslation();
  const [isTimeZoneOpen, setIsTimeZoneOpen] = React.useState(false);

  const [currentLang, setCurrentLang] = React.useState(getInitialLanguage);

  const location = useLocation();
  const navigate = useNavigate();

  const isLogIn = !!studioUserStore.isLoggedIn;
  const email = studioUserStore.me?.email?.split("@")[0] ?? "";

  React.useEffect(() => {
    if (!isLogIn && location.pathname !== "/") {
      navigate("/");
    } else if (isLogIn && location.pathname === "/") {
      navigate(studioUserStore?.hasPermission("menu:content:view") ? "/block" : "/salesStatistics");
    }
  }, [isLogIn, location.pathname, navigate]);

  React.useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, []);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value;
    setCurrentLang(lang);
    i18n.changeLanguage(lang); 
  };


  const hideHeader = location.pathname === "/";

  return (
    <Provider>
        <div>
         {!hideHeader && (
              <Navbar
                expand="xxl"
                className="position-relative z-3 px-2 py-0"
                style={{
                  backgroundColor: "#645AD2",
                  minHeight: "60px", 
                  boxSizing: "border-box",
                }}
              >
                {/* 로고 */}
                <Navbar.Brand as={NavLink} to="/">
                  <img src={PoluvLogo} width={157} height={53} />
                </Navbar.Brand>

                {/* 업로드 드롭다운 */}
                {isLogIn && studioUserStore?.hasPermission("menu:content:view") && (
                  <NavDropdown
                    label={t("nav-upload")}
                    items={[
                      { to: "/license", label: t("nav-license"), isAllowed: studioUserStore?.hasPermission("license:view") },
                      { to: "/block", label: t("nav-design"), isAllowed: studioUserStore?.hasPermission("block:view") },
                      { to: "/category", label: t("nav-category"), isAllowed: studioUserStore?.hasPermission("category:view") },
                      { to: "/categoryPackage", label: t("nav-category-package"), isAllowed: studioUserStore?.hasPermission("categoryPackage:view") },
                      { to: "/mode", label: t("nav-mode"), isAllowed: studioUserStore?.hasPermission("mode:view") },
                    ]
                    .filter(i => i.isAllowed)
                    .map(i => ({ to: i.to, label: i.label}))}
                  />
                )}

                {isLogIn && (
                  <>
                    <Nav className="me-auto d-flex flex-row flex-nowrap align-items-center">
                      {[
                          { to: "/layouts", label: t("nav-layout"), isAllowed: studioUserStore?.hasPermission("layout:view") },
                          { to: "/published", label: t("nav-ip"), isAllowed: studioUserStore?.hasPermission("publish:view") },
                          { to: "/template", label: t("nav-template"), isAllowed: studioUserStore?.hasPermission("template:view") },
                          {
                            label: t("nav-sales"),
                            isAllowed: studioUserStore?.hasPermission("menu:sales:view"),
                            items: [
                              { to: "/salesStatistics", label: t("nav-sales-statistics") },
                              { to: "/salesDetail", label: t("nav-sales-detail") },
                            ],
                          },
                          { to: "/accountPermission", label: t("nav-permission"), isAllowed: studioUserStore?.hasPermission("menu:permission") },
                        ]
                        .filter(({ isAllowed }) => isAllowed)
                        .map(({ to, label, items }) => (
                          items ? (
                            <NavDropdown key={label} label={label} items={items} />
                          ) : (
                            <Nav.Link
                              key={to}
                              as={NavLink}
                              to={to}
                              style={{
                                fontSize: "16px",
                                lineHeight: "45px",
                                whiteSpace: "nowrap",
                                color: "#fff",
                                textDecoration: "none",
                                padding: "0 8px",
                                fontWeight: location.pathname === to ? "bold" : "normal",
                                borderRadius: "6px",
                              }}
                            >
                              {label}
                            </Nav.Link>
                          )
                        ))}
                    </Nav>
                      {/* 우측 컨트롤 */}
                      <div className="d-flex gap-2 align-items-center ms-auto order-xxl-2 order-3">

                        {/* 로그인 유저 아이디 정보 */}
                        <Navbar.Text className="ms-auto me-3 d-flex align-items-center gap-2 flex-nowrap">
                          <PersonCircle size={28} className="text-white" />
                          <span className="small text-white">{email}</span>
                        </Navbar.Text>
                        
                        {/* 언어 선택 */}
                        <Form.Select
                          value={currentLang}
                          onChange={handleLanguageChange}
                          style={{
                            backgroundColor: "#A29CE4",
                            color: "#fff",
                            border: "none",
                            fontWeight: "bold",
                            boxShadow: "none",
                            cursor: "pointer",
                            width: "130px",
                          }}
                        >
                          {NativeLanguageNameMap.map(({ code, label }) => (
                            <option key={code} value={code}>{label}</option>
                          ))}
                        </Form.Select>

                        {/* TimeZone */}
                        <div
                          className="d-flex align-items-center gap-1 px-2 rounded"
                          style={{ backgroundColor: "#A29CE4", width: "190px" }}
                        >
                          <Clock size={25} color="#fff" className="ms-1" />
                          <Form.Select
                            value={blockKitStore.timeZone}
                            onMouseDown={() => setIsTimeZoneOpen(prev => !prev)}
                            onBlur={() => setIsTimeZoneOpen(false)}
                            onChange={e => {
                              blockKitStore.setTimeZone(e.target.value);
                              setIsTimeZoneOpen(false);
                            }}
                            style={{
                              backgroundColor: "#A29CE4",
                              color: "#fff",
                              border: "none",
                              fontWeight: "bold",
                              boxShadow: "none",
                              cursor: "pointer"
                            }}
                          >
                            {blockKitStore.supportedTimeZones.map(t =>
                              <option value={t} key={t}>{t}</option>
                            )}
                          </Form.Select>
                        </div>

                        {/* 가이드 버튼 */}
                        {studioUserStore?.hasPermission("guide:view") && (
                          <Button
                            onClick={() => {
                              window.open(
                                "https://youngint.notion.site/_2026ver-2d9d70e4715280598afaca51d99d6b68?source=copy_link",
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                            className="d-flex align-items-center gap-2 fw-bold border-0 text-nowrap"
                            style={{ backgroundColor: "#A29CE4" }}
                          >
                            <JournalBookmark size={17} />
                            {t("nav-guide")}
                          </Button>
                        )}

                        {/* 로그아웃 */}
                        <Button
                          variant="link"
                          onClick={async () => {
                            await studioUserStore.logOutAsync();
                            navigate("/");
                          }}
                          className="fw-normal text-nowrap text-decoration-none"
                          style={{ color: "#FFD246" }}
                        >
                          {t("nav-logout")}
                        </Button>
                      </div>

                  </>
                )}
              </Navbar>
            )}
            <Routes>
              <Route path="/" element={<PolaPolarStudioHomePage/>}/>
              {isLogIn && studioUserStore?.hasPermission("block:view") && <Route path="/block" element={<BtsPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("layout:view") && <Route path="/layouts" element={<LayoutPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("category:view") && <Route path="/category" element={<CategoryPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("block:view") && <Route path="/category/:categoryId" element={<BlockPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("categoryPackage:view") && <Route path="/categoryPackage" element={<CategoryPackagePage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("license:view") && <Route path="/license" element={<LicensePage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("mode:view") && <Route path="/mode" element={<ModePage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("publish:view") && <Route path="/published" element={<PublishedCategoryPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("publish:view") && <Route path="/published/:categoryId" element={<PublishedBlockPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("template:view") && <Route path="/template" element={<TemplatePage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("menu:sales:view") &&<Route path="/salesStatistics" element={<SalesStatisticsPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("menu:sales:view") && <Route path="/salesDetail" element={<SalesDetailPage/>}/>}
              {isLogIn && studioUserStore?.hasPermission("permission:view") && <Route path="/accountPermission" element={<AccountPermissionPage/>}/>}
            </Routes>
        </div>
    </Provider>
  );
}))

export default BtsIndex;
